import type { XhrStatus } from 'redux/reusable/xhrStatus';
import { createXhrStatusModule } from 'redux/reusable/xhrStatus';
import type {
  Entry,
  EntryIdPredictionsMap,
  PredictedProjectsMap,
  SuggestedEntry,
  ValidGroupStatus,
} from 'types/timeline';
import type { UserTimeFormat } from 'types/user';
import { actionCreatorFactory } from 'typescript-fsa';

// This is a good place to create the xhrStatus module, since this file does not have many dependencies
export const timelineXhrModule = createXhrStatusModule('timeline');

// Create an action namespace
const actionCreator = actionCreatorFactory('timely/timeline');

export const fetchingSuggestedEntries = actionCreator<{ date: string }>(
  'FETCHING_SUGGESTED_ENTRIES'
);
export const fetchedSuggestedEntries = actionCreator<{
  entries: Array<SuggestedEntry>;
  date: string;
  excludeTrackerEntries: string | null;
}>('FETCHED_SUGGESTED_ENTRIES');
export const suggestedEntriesPusherUpdate = actionCreator('SUGGESTED_ENTRIES_PUSHER_UPDATE');
export const setFilter = actionCreator<{
  key: keyof Filter;
  value: any; // guard in action creator
}>('SET_FILTER');
export const setTimelineStyle = actionCreator<TimelineStyleTypes>('SET_TIMELINE_STYLE');
export const toggleExpandGroupRef = actionCreator<string>('TOGGLE_EXPAND_GROUP_REF');
export const toggleExpandEntryId = actionCreator<number>('TOGGLE_EXPAND_ENTRY_ID');
export const toggleSelectEntryIds = actionCreator<{
  entry_ids: Array<number>;
}>('TOGGLE_SELECT_ENTRY_IDS');
export const clearSelectedEntryIds = actionCreator('CLEAR_SELECTED_ENTRY_IDS');
export const markEntryIdsAsSpam = actionCreator<{
  entry_ids: Array<number>;
}>('MARK_ENTRY_IDS_AS_SPAM');
export const markEntryIdsAsNotSpam = actionCreator<{
  entry_ids: Array<number>;
}>('MARK_ENTRY_IDS_AS_NOT_SPAM');
export const deleteEntryIds = actionCreator<{
  entry_ids: Array<number>;
}>('DELETE_ENTRY_IDS');
export const clearSelectedMemories = actionCreator('CLEAR_SELECTED_MEMORIES');
export const setSelectedMemories = actionCreator('SET_SELECTED_MEMORIES');
export const predictGroupRefsToProjectId = actionCreator<{
  groupRefs: Array<string>;
  projectId: number;
}>('PREDICT_GROUP_REFS_TO_PROJECT_ID');
export const preserveScrollPosition = actionCreator<number | null>('PRESERVE_SCROLL_POSITION');
export const setGutterHighlight = actionCreator<TimelineGutterHighlight | null>(
  'SET_GUTTER_HIGHLIGHT'
);
export const setPixelsPerMinute = actionCreator<number>('SET_PIXELS_PER_MINUTE');
export const setTimestampSnappingMinutes = actionCreator<number>('SET_TIMESTAMP_SNAPPING_MINUTES');
export const ALLOWED_TIMESTAMP_SNAPPING_VALUES = [1, 2, 3, 4, 5, 6, 10, 12, 15, 20, 30];
export const setShowAiIgnoredMemoriesModal = actionCreator<boolean>(
  'SET_SHOW_AI_IGNORED_MEMORIES_MODAL'
);

type FilterMode = 'timeline' | 'group';

export enum TimelineSortTypes {
  sortByTime = 'sortByTime',
  sortByGroupDuration = 'sortByGroupDuration',
  sortByAiImportance = 'sortByAIImportance',
}

export enum TimelineStyleTypes {
  timespansOnTimeline = 'timespansOnTimeline',
  timeEntriesOnTimeline = 'timeEntriesOnTimeline',
}

export interface TimelineGutterHighlight {
  top: number;
  height: number;
}

export interface Filter {
  mode: 'timeline' | 'group';
  expandedEntryIds: Array<number>;
  expandGroupRefs: Array<string>;
  displayTimelineRules: Array<ValidGroupStatus>;
  displayGroupRules: Array<ValidGroupStatus>;
  noiseMinThreshold: number;
  importanceMinThreshold: number;
  searchQuery: string;
  timeFormat: UserTimeFormat;
  timelineSort: TimelineSortTypes;
}

export interface RelevantMemoryFilters {
  date: string;
  expandedEntryIds: Array<number>;
  noiseMinThreshold: number;
  importanceMinThreshold: number;
  mode: FilterMode;
  searchQuery: string;
  timeFormat: UserTimeFormat;
}

export interface RelevantMetaMemoryFilters {
  date: string | null;
  timeFormat: UserTimeFormat;
}

export interface TimelineEntrySchema {
  [hour_id: number]: Entry;
}

export interface DefaultEntities {
  timeline: TimelineEntrySchema;
}

export interface State {
  selection: {
    entryIds: Array<number>;
  };
  filter: Filter;
  pixelsPerMinute: number;
  timestampSnappingMinutes: number;
  groups: Array<SuggestedEntry>;
  projectPredictions: PredictedProjectsMap;
  entryIdPredictions: EntryIdPredictionsMap;
  entities: DefaultEntities;
  result: Array<number>;
  resultMeta: {
    /** Keep track of what we are currently fetched */
    fetchingForDate: string;
    /** Keep track of which day we have results for */
    fetchedForDate: string;
    /** Keep track of whether the result excluded tracker entries */
    excludedTrackerEntries: string | null;
  };
  scrollPostion: number | null;
  gutterHighlight?: TimelineGutterHighlight;
  style?: TimelineStyleTypes;
  lastPusherUpdate: null | string;
  xhrStatus: XhrStatus;
  showAiIgnoredMemoriesModal: boolean;
}

export const SINGLE_MEMORY_HEIGHT = 50;
export const MEMORY_GROUP_HEADER_HEIGHT = 60;
export const SINGLE_MEMORY_ITEM_HEIGHT = 18;
